import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'semantic-ui-react';
import './DatabaseSyncOverlay.css';

/**
 * DatabaseSyncOverlay —— 数据库结构同步全屏进度遮罩
 *
 * 触发：App 挂载后调用 /api/database/schema-check，发现结构差异即全屏展示，
 * 并通过 /api/database/sync-stream（SSE）流式接收逐表迁移进度。
 *
 * 步数由服务端 start 事件下发（total），前端不再硬编码 —— 否则后端调整
 * schemaTargets 后前端会显示不存在的步骤、进度数字前后矛盾。
 *
 * 设计语言对齐 StudioPage：靛蓝 #4f63a8 + 点阵纹理 + 呼吸光圈 + 黑金主按钮。
 * 动画尊重 prefers-reduced-motion。空数据不造假：无差异时不渲染任何遮罩。
 *
 * 修改时间：2026-08-28
 */

const POLL_INTERVAL = 12 * 60 * 60 * 1000; // 12h 内不再重复检查
const LS_KEY = 'db_schema_check_at';
const LS_DONE_KEY = 'db_schema_synced_at';

// 同步中超过该时长没有任何事件即判为卡死（迁移逐表执行，正常不会这么久无输出）
const STALL_TIMEOUT = 45 * 1000;
// done 后自动收起遮罩，避免遮住整个后台
const DONE_AUTO_HIDE = 1400;

const readStamp = (key) => {
  try {
    const v = Number(localStorage.getItem(key) || 0);
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
};

const writeStamp = (key) => {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    /* 隐私模式忽略 */
  }
};

export default function DatabaseSyncOverlay() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('idle'); // idle | checking | syncing | done | error
  const [steps, setSteps] = useState([]); // {table, status: pending|ok}
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(0);
  const esRef = useRef(null);
  const tickRef = useRef(null);
  const hideRef = useRef(null);
  const stallRef = useRef(null);
  const doneRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (hideRef.current) {
      clearTimeout(hideRef.current);
      hideRef.current = null;
    }
    if (stallRef.current) {
      clearTimeout(stallRef.current);
      stallRef.current = null;
    }
  }, []);

  // 每次收到服务端事件就重置卡死看门狗
  const armStallWatchdog = useCallback(() => {
    if (stallRef.current) clearTimeout(stallRef.current);
    stallRef.current = setTimeout(() => {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      clearTimers();
      setError(t('database_sync.stalled'));
      setPhase('error');
    }, STALL_TIMEOUT);
  }, [clearTimers, t]);

  const startSync = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
    }
    clearTimers();
    doneRef.current = false;
    setPhase('syncing');
    setError('');
    setSteps([]);
    setPercent(0);
    startedAt.current = Date.now();
    tickRef.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - startedAt.current) / 100) / 10);
    }, 200);

    const es = new EventSource('/api/database/sync-stream');
    esRef.current = es;
    armStallWatchdog();

    // 服务端先下发总步数，据此预置清单（不再硬编码步数）
    es.addEventListener('start', (e) => {
      armStallWatchdog();
      try {
        const d = JSON.parse(e.data);
        const total = Number(d?.total) || 0;
        if (total > 0) {
          setSteps(
            Array.from({ length: total }, () => ({
              table: '',
              status: 'pending',
            }))
          );
        }
      } catch {
        /* 忽略坏帧 */
      }
    });

    es.addEventListener('progress', (e) => {
      armStallWatchdog();
      try {
        const d = JSON.parse(e.data);
        setSteps((prev) => {
          const next = prev.length ? prev.slice() : [];
          while (next.length < d.total) {
            next.push({ table: '', status: 'pending' });
          }
          // 填充到当前步之前的空表名
          for (let i = 0; i < d.step - 1 && i < next.length; i++) {
            if (!next[i].table) {
              next[i] = {
                ...next[i],
                table: t('database_sync.step_fallback', { index: i + 1 }),
                status: 'ok',
              };
            }
          }
          next[d.step - 1] = { table: d.label, status: 'ok' };
          return next;
        });
        setPercent(d.percent);
      } catch {
        /* 忽略坏帧 */
      }
    });

    es.addEventListener('done', () => {
      doneRef.current = true;
      clearTimers();
      setPercent(100);
      setPhase('done');
      writeStamp(LS_DONE_KEY);
      es.close();
      esRef.current = null;
      // 自动收起：原实现没有任何关闭路径，遮罩会永久盖住后台
      hideRef.current = setTimeout(() => setPhase('idle'), DONE_AUTO_HIDE);
    });

    es.addEventListener('error', (e) => {
      clearTimers();
      es.close();
      esRef.current = null;
      if (e.data) {
        // 服务端发来 error 事件：真实失败
        try {
          setError(JSON.parse(e.data).message || t('database_sync.failed'));
        } catch {
          setError(t('database_sync.failed'));
        }
        setPhase('error');
        return;
      }
      if (doneRef.current) {
        // 正常收流（done 之后连接关闭）：已在上面的 done 分支处理
        return;
      }
      // 连接层中断且从未收到 done：同步很可能只做了一半，
      // 绝不能当成成功并记完成戳，否则会被 12h 节流掩盖。
      setError(t('database_sync.interrupted'));
      setPhase('error');
    });
  }, [armStallWatchdog, clearTimers, t]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const now = Date.now();
      // 已知结构同步完成过，且 12h 内不重复检查
      if (now - readStamp(LS_DONE_KEY) < POLL_INTERVAL) return;
      if (now - readStamp(LS_KEY) < 60_000) return; // 1 分钟节流
      writeStamp(LS_KEY);
      setPhase('checking');
      try {
        const res = await fetch('/api/database/schema-check', {
          credentials: 'include',
        });
        if (cancelled) return;
        if (res.status === 401) {
          // 未登录（首次部署/访客）：静默跳过，登录后 PrivateRoute 场景会再触发
          setPhase('idle');
          return;
        }
        const body = await res.json();
        if (!body?.success) {
          setPhase('idle');
          return;
        }
        if (body.data?.ok) {
          // 结构一致：记录完成戳，避免下次再查
          writeStamp(LS_DONE_KEY);
          setPhase('idle');
          return;
        }
        // 有差异 → 全屏展示并自动开始 SSE 同步
        startSync();
      } catch {
        if (!cancelled) setPhase('idle');
      }
    };
    check();
    return () => {
      cancelled = true;
      clearTimers();
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [startSync, clearTimers]);

  if (phase === 'idle' || phase === 'checking') return null;

  const doneCount = steps.filter((s) => s.status === 'ok').length;
  const total = steps.length;

  return (
    <div
      className='db-sync-overlay'
      role='dialog'
      aria-label={t('database_sync.aria_label')}
    >
      <div className='db-sync-overlay__grid' aria-hidden='true' />
      <div className='db-sync-card'>
        <div className='db-sync-card__badge'>
          <span className='db-sync-card__badge-ring' aria-hidden='true' />
          <Icon name='database' />
        </div>
        <span className='db-sync-card__kicker'>SCHEMA SYNC</span>
        <h1>
          {phase === 'done' && t('database_sync.title_done')}
          {phase === 'error' && t('database_sync.title_error')}
          {phase === 'syncing' && t('database_sync.title_syncing')}
        </h1>
        <p className='db-sync-card__desc'>
          {phase === 'done'
            ? t('database_sync.desc_done')
            : phase === 'error'
            ? t('database_sync.desc_error')
            : t('database_sync.desc_syncing')}
        </p>

        <div
          className='db-sync-bar'
          role='progressbar'
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className='db-sync-bar__fill' style={{ width: `${percent}%` }} />
        </div>

        <div className='db-sync-meta'>
          <span>
            {phase === 'done' || phase === 'error'
              ? t('database_sync.progress_of', {
                  done: doneCount,
                  total: total,
                })
              : t('database_sync.migrating', {
                  step: Math.min(doneCount + 1, total),
                  total: total,
                })}
          </span>
          <span className='db-sync-meta__right'>
            {phase === 'syncing'
              ? `${percent}% · ${elapsed.toFixed(1)}s`
              : `${percent}%`}
          </span>
        </div>

        <ul className='db-sync-steps'>
          {steps.map((s, i) => (
            <li
              key={i}
              className={`db-sync-step is-${s.status}${
                s.table ? '' : ' is-anon'
              }`}
            >
              <span className='db-sync-step__icon'>
                {s.status === 'ok' && <Icon name='check' />}
                {s.status === 'pending' && <Icon name='ellipsis horizontal' />}
              </span>
              <span className='db-sync-step__label'>
                {s.table ||
                  t('database_sync.step_fallback', { index: i + 1 })}
              </span>
            </li>
          ))}
        </ul>

        {phase === 'error' && (
          <div className='db-sync-error'>
            <Icon name='warning circle' />
            <span>{error}</span>
          </div>
        )}
        {phase === 'done' && (
          <div className='db-sync-done-note'>
            <Icon name='shield alternate' />
            <span>{t('database_sync.done_note')}</span>
          </div>
        )}

        {phase === 'error' && (
          <div className='db-sync-actions'>
            <button
              type='button'
              className='db-sync-btn db-sync-btn--primary'
              onClick={startSync}
            >
              {t('database_sync.retry')}
            </button>
            <button
              type='button'
              className='db-sync-btn'
              onClick={() => setPhase('idle')}
            >
              {t('database_sync.dismiss')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
